// P1-009: Prepare Phase Coordinator — produces PrepareResult
// Matches ARCH SS5.1 pattern
import type { PrepareResult, DeployedAgent, HardwareSnapshot, SkillSnapshot } from '@/shared/types/phase-contracts';
import { useAgentStore } from '../stores/agentStore';
import type { OwnedAgent } from '../stores/agentStore';
import { useHardwareStore } from '../stores/hardwareStore';
import { AgentDeployer } from './AgentDeployer';
import { OverloadCalculator } from './OverloadCalculator';

export interface FinalizeResult {
  success: boolean;
  prepareResult?: PrepareResult;
  errors?: string[];
}

export const PreparePhase = {
  /**
   * Finalize the prepare phase:
   * 1. Validate formation (5 agents, no duplicates)
   * 2. Check compute cost <= total compute
   * 3. Build PrepareResult with level-calculated stats
   */
  finalize(): FinalizeResult {
    // Validate
    const validation = AgentDeployer.fullCheck();
    if (!validation.valid) {
      return { success: false, errors: validation.errors };
    }

    const agentStore = useAgentStore.getState();
    const hwStore = useHardwareStore.getState();
    const formationAgents = agentStore.getFormationAgents();

    // Build DeployedAgent array (stats already calculated with level in agentStore)
    const team: DeployedAgent[] = formationAgents.map((agent) =>
      buildDeployedAgent(agent)
    );

    // Build HardwareSnapshot
    const powerUsage = hwStore.getTotalPower();
    const powerLimit = hwStore.getTotalPowerLimit();
    const heatGen = hwStore.getTotalHeatGen();
    const cooling = hwStore.getTotalCooling();
    const overload = OverloadCalculator.evaluate(powerUsage, powerLimit, heatGen, cooling);
    const teamComputeCost = AgentDeployer.calculateTeamComputeCost();

    const hardwareSnapshot: HardwareSnapshot = {
      totalCompute: hwStore.getTotalCompute(),
      usedCompute: teamComputeCost,
      totalPowerLimit: powerLimit,
      currentPowerUsage: powerUsage,
      totalCooling: cooling,
      currentHeatGen: heatGen,
      overloadValue: overload.overloadValue,
      overloadLevel: overload.overloadLevel,
      specialEffects: hwStore.getSpecialEffects(),
    };

    const prepareResult: PrepareResult = {
      team,
      hardwareSnapshot,
      preparedAt: Date.now(),
    };

    return { success: true, prepareResult };
  },
};

function buildDeployedAgent(agent: OwnedAgent): DeployedAgent {
  const activeSkill: SkillSnapshot = {
    skillId: agent.activeSkill.skillId,
    name: agent.activeSkill.name,
    description: agent.activeSkill.description,
    cooldown: agent.activeSkill.cooldown,
    effects: [...agent.activeSkill.effects],
  };

  return {
    agentId: agent.configId,
    name: agent.name,
    type: agent.type,
    rarity: agent.rarity,
    level: agent.level,
    attack: agent.attack,
    defense: agent.defense,
    hp: agent.hp,
    critRate: agent.critRate,
    computeCost: agent.computeCost,
    elementAffinity: agent.elementAffinity,
    activeSkill,
  };
}
